"use client";

import { useCallback, useState, type JSX } from "react";
import Box from "@/shared/components/common/Box";
import Flex from "@/shared/components/common//Flex";
import Grid from "@/shared/components/common/Grid";
import Typography from "@/shared/components/common/Typography";
import List from "@/shared/components/common/List";
import { useDeviceType } from "@/shared/context/DeviceContext";
import { Button } from "@/shared/components/common/button";
import { CaretDown, CaretUp } from "@phosphor-icons/react/dist/ssr";
import json from "@/public/data/hotel/components/nearDistance.json";

interface NearDistance {
  distances: Distance[];
  map?: JSX.Element | null;
}

export default function NearDistance({ distances, map }: NearDistance) {
  const [showMore, setShowMore] = useState(false);
  const device = useDeviceType();

  const { showLess, showMoreText } = json;

  const mappedDistance = distances.map((distance) => ({
    ...distance,
    id: distance.title,
  }));

  const deviceDistanceDate =
    device === "mobile"
      ? showMore
        ? mappedDistance
        : mappedDistance.slice(0, 3)
      : mappedDistance;

  const handleShowMore = useCallback(() => setShowMore((prev) => !prev), []);

  return (
    <Grid className="w-full  bg-background md:shadow-1 md:border border-border rounded md:rounded-xs max-sm:border p-3 ">
      <Grid.GridCol className="col-span-7 max-md:col-span-12 max-md:order-2 max-md:mt-3">
        <Grid.GridCol
          className={`col-span-7 max-md:col-span-12 h-80 max-md:!h-60 ${
            showMore && "overflow-y-scroll max-md:!h-64"
          } md:overflow-y-scroll overflow-x-hidden [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar]:w-[4px] `}
        >
          <List
            items={deviceDistanceDate}
            renderItems={({ distance, title, travel_time }) => (
              <Grid className="w-full max-md:flex-col max-md:gap-3 p-2 hover:bg-muted dark:hover:bg-muted/10 rounded-md cursor-pointer">
                <Typography className="max-md:font-semibold grid col-span-7 max-md:col-span-12">
                  {title}
                </Typography>
                <Grid className="col-span-5">
                  <Flex className="gap-2">
                    <Flex className="min-w-[100px] justify-start gap-1">
                      <Typography
                        className="font-semibold text-start "
                        variant="caption"
                      >
                        {`${travel_time.value} ${travel_time.unit}`}
                      </Typography>
                      <Typography
                        className="font-semibold text-gray-700 text-start "
                        variant="caption"
                      >
                        {travel_time.mode}
                      </Typography>
                    </Flex>

                    <Typography
                      className="font-semibold min-w-[100px] text-right"
                      variant="caption"
                    >
                      {`${distance.value} ${distance.unit}`}
                    </Typography>
                  </Flex>
                </Grid>
                <Box className="h-px col-span-12 bg-gray-300 md:hidden" />
              </Grid>
            )}
          />
        </Grid.GridCol>
        <Button
          variant="ghost"
          onClick={handleShowMore}
          className="text-primary gap-2 w-full md:hidden mt-3 !bg-background"
        >
          {showMore ? showLess : showMoreText}
          {showMore ? <CaretUp /> : <CaretDown />}
        </Button>
      </Grid.GridCol>

      <Grid.GridCol className="col-span-5 max-md:col-span-12">
        <Box className="h-80 max-md:h-56">{map}</Box>
      </Grid.GridCol>
    </Grid>
  );
}
